import { z } from "zod";
import {
  apiErrorSchema, planPreviewSchema, requestDetailSchema, requestListSchema, sessionSchema,
} from "../../../shared/labels/contracts";
import type {
  CommandMeta, CreateRequestInput, DecisionInput, ListQuery, PlanPreview, PrintInput,
  RequestDetail, RequestList, Requirements, Role, SessionInfo,
} from "../../../shared/labels/contracts";
import {
  labelWorkOrderLookupResponseSchema, labelWorkOrderReviewResponseSchema, labelWorkOrderCreateResultSchema,
  labelPlanSourceListResponseSchema, labelPlanSourceDetailSchema, labelPlanSourceLinkResultSchema,
  labelPreparedPlanSourceCatalogSchema,
} from "../../../shared/labels/work-order-request-contracts";
import type {
  LabelWorkOrderLookupResponse, LabelWorkOrderReviewResponse, LabelWorkOrderCreateResult,
  LabelPlanSourceListResponse, LabelPlanSourceDetail, LabelPlanSourceLinkResult,
  labelWorkOrderReviewRequestSchema, labelWorkOrderCreateRequestSchema, labelPlanSourceLinkRequestSchema,
} from "../../../shared/labels/work-order-request-contracts";
import type { UiFault, UploadDraft } from "./ui-types";

const pinChallengeSchema = z.object({
  challengeId: z.string().uuid(), status: z.literal("accepted"), expiresInSeconds: z.number().positive(),
});
const identityProofSchema = z.object({ userId: z.string(), tenantId: z.string(), verifiedAt: z.string() });
export type PinChallenge = z.infer<typeof pinChallengeSchema>;
export type IdentityProof = z.infer<typeof identityProofSchema>;

export class LabelApiError extends Error {
  constructor(public readonly fault: UiFault) { super(fault.message); this.name = "LabelApiError"; }
}

export function uiFault(error: unknown): UiFault {
  if (error instanceof LabelApiError) return error.fault;
  return { code: "UNEXPECTED", message: "İşlem sonucu doğrulanamadı. Kaydı yenileyip tekrar kontrol edin." };
}

export interface LabelsApi {
  preparedPlanSources(signal?: AbortSignal): Promise<z.infer<typeof labelPreparedPlanSourceCatalogSchema>>;
  savePreparedPlanSource(key: "2026-week-45", idempotencyKey: string, signal?: AbortSignal): Promise<PlanPreview>;
  lookupWorkOrder(workOrder: string, signal?: AbortSignal): Promise<LabelWorkOrderLookupResponse>;
  reviewWorkOrder(input: z.infer<typeof labelWorkOrderReviewRequestSchema>, signal?: AbortSignal): Promise<LabelWorkOrderReviewResponse>;
  createWorkOrder(input: z.infer<typeof labelWorkOrderCreateRequestSchema>, key: string, signal?: AbortSignal): Promise<LabelWorkOrderCreateResult>;
  ownWorkOrderRequests(query: ListQuery, signal?: AbortSignal): Promise<RequestList>;
  planSources(signal?: AbortSignal): Promise<LabelPlanSourceListResponse>;
  planSource(id: string, signal?: AbortSignal): Promise<LabelPlanSourceDetail>;
  linkPlanSource(sourceId: string, rowId: string, input: z.infer<typeof labelPlanSourceLinkRequestSchema>, key: string, signal?: AbortSignal): Promise<LabelPlanSourceLinkResult>;
  session(signal?: AbortSignal): Promise<SessionInfo>;
  demoSession(role: Role, key: string, signal?: AbortSignal): Promise<SessionInfo>;
  list(query: ListQuery, signal?: AbortSignal): Promise<RequestList>;
  detail(id: string, signal?: AbortSignal): Promise<RequestDetail>;
  create(input: CreateRequestInput, key: string, signal?: AbortSignal): Promise<RequestDetail>;
  assign(id: string, salesUserId: string, meta: CommandMeta, signal?: AbortSignal): Promise<RequestDetail>;
  saveDraft(id: string, requirements: Requirements, meta: CommandMeta, signal?: AbortSignal): Promise<RequestDetail>;
  submitSales(id: string, meta: CommandMeta, signal?: AbortSignal): Promise<RequestDetail>;
  upload(id: string, upload: UploadDraft, meta: CommandMeta, signal?: AbortSignal): Promise<RequestDetail>;
  submitReview(id: string, fileVersionId: string, meta: CommandMeta, signal?: AbortSignal): Promise<RequestDetail>;
  decide(id: string, input: DecisionInput, meta: CommandMeta, signal?: AbortSignal): Promise<RequestDetail>;
  declarePrint(id: string, input: PrintInput, meta: CommandMeta, signal?: AbortSignal): Promise<RequestDetail>;
  reportPrintIssue(id: string, reason: string, meta: CommandMeta, signal?: AbortSignal): Promise<void>;
  reuseFile(id: string, fileVersionId: string, meta: CommandMeta, signal?: AbortSignal): Promise<RequestDetail>;
  retryNotification(id: string, jobId: string, meta: CommandMeta, signal?: AbortSignal): Promise<RequestDetail>;
  previewPlan(file: File, sheetName: string, key: string, signal?: AbortSignal): Promise<PlanPreview>;
  demoSalesPlan(signal?: AbortSignal): Promise<PlanPreview | null>;
  file(id: string, fileId: string, signal?: AbortSignal): Promise<Blob>;
  requestPin(identifier: string, key: string, signal?: AbortSignal): Promise<PinChallenge>;
  verifyPin(challengeId: string, code: string, key: string, signal?: AbortSignal): Promise<IdentityProof>;
  loginPin(pin: string, signal?: AbortSignal): Promise<{ authenticated: true }>;
  logoutPin(signal?: AbortSignal): Promise<void>;
}

export function createLabelsApi(csrfToken: string | null, fetcher: typeof fetch = fetch): LabelsApi {
  const base = "/api/labels";
  async function transport(path: string, method: string, body: BodyInit | undefined, key: string | undefined, signal: AbortSignal | undefined, binary = false) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) controller.abort();
    signal?.addEventListener("abort", abort, { once: true });
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 30_000);
    const headers = new Headers({ Accept: binary ? "application/octet-stream, application/pdf, image/png, image/jpeg" : "application/json" });
    if (body !== undefined && !(body instanceof FormData)) headers.set("Content-Type", "application/json");
    if (method !== "GET") {
      if (csrfToken) headers.set("X-CSRF-Token", csrfToken);
      if (key) headers.set("Idempotency-Key", key);
    }
    try {
      const response = await fetcher(base + path, { method, body, headers, credentials: "same-origin", signal: controller.signal });
      if (!response.ok) {
        let payload: unknown;
        try { payload = await response.json(); } catch { payload = null; }
        const parsed = apiErrorSchema.safeParse(payload);
        if (parsed.success) throw new LabelApiError({ ...parsed.data.error, status: response.status });
        throw new LabelApiError({ code: "HTTP_" + response.status, message: "Sunucu işlemi doğrulayamadı. Kaydı yenileyerek tekrar kontrol edin.", status: response.status });
      }
      // Consume the entire body while timeout and cancellation still apply.
      const bodyBytes = await response.arrayBuffer();
      return new Response(response.status === 204 ? null : bodyBytes, { status: response.status, statusText: response.statusText, headers: response.headers });
    } catch (error: unknown) {
      if (error instanceof LabelApiError) throw error;
      if (signal?.aborted) throw new LabelApiError({ code: "ABORTED", message: "Eski ekran isteği iptal edildi." });
      throw new LabelApiError({
        code: timedOut ? "TIMEOUT" : "NETWORK",
        message: method === "GET" ? "Veri alınamadı. Bağlantıyı kontrol edip yeniden deneyin." : "İşlemin sunucuda tamamlanıp tamamlanmadığı doğrulanamadı. Aynı işlem anahtarıyla yeniden deneyin veya kaydı yenileyin.",
      });
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener("abort", abort);
    }
  }
  async function request<T>(path: string, method: string, body: BodyInit | undefined, schema: z.ZodType<T>, key?: string, signal?: AbortSignal): Promise<T> {
    const response = await transport(path, method, body, key, signal);
    let payload: unknown;
    try { payload = await response.json(); } catch { throw new LabelApiError({ code: "INVALID_RESPONSE", message: "Sunucu yanıtı okunamadı; işlem tamamlandı kabul edilmedi." }); }
    const parsed = schema.safeParse(payload);
    if (!parsed.success) throw new LabelApiError({ code: "INVALID_RESPONSE", message: "Sunucu verisi beklenen sözleşmeyle uyuşmuyor; güncel kayıt doğrulanamadı." });
    return parsed.data;
  }
  const mutation = (id: string, path: string, method: string, input: Record<string, unknown>, meta: CommandMeta, signal?: AbortSignal) =>
    request("/requests/" + encodeURIComponent(id) + path, method, JSON.stringify({ ...input, expectedVersion: meta.expectedVersion }), requestDetailSchema, meta.idempotencyKey, signal);
  return {
    preparedPlanSources: (signal) => request("/plan-sources/prepared", "GET", undefined, labelPreparedPlanSourceCatalogSchema, undefined, signal),
    savePreparedPlanSource: (key, idempotencyKey, signal) => request("/plan-sources/prepared", "POST", JSON.stringify({ key }), planPreviewSchema, idempotencyKey, signal),
    lookupWorkOrder: (workOrder, signal) => request("/work-order-requests/lookup", "POST", JSON.stringify({ workOrder }), labelWorkOrderLookupResponseSchema, undefined, signal),
    reviewWorkOrder: (input, signal) => request("/work-order-requests/review", "POST", JSON.stringify(input), labelWorkOrderReviewResponseSchema, undefined, signal),
    createWorkOrder: (input, key, signal) => request("/work-order-requests", "POST", JSON.stringify(input), labelWorkOrderCreateResultSchema, key, signal),
    ownWorkOrderRequests: (query, signal) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) if (value !== undefined && value !== "") params.set(key, String(value));
      return request("/work-order-requests/mine?" + params.toString(), "GET", undefined, requestListSchema, undefined, signal);
    },
    planSources: (signal) => request("/plan-sources", "GET", undefined, labelPlanSourceListResponseSchema, undefined, signal),
    planSource: (id, signal) => request("/plan-sources/" + encodeURIComponent(id), "GET", undefined, labelPlanSourceDetailSchema, undefined, signal),
    linkPlanSource: (sourceId, rowId, input, key, signal) => request("/plan-sources/" + encodeURIComponent(sourceId) + "/rows/" + encodeURIComponent(rowId) + "/links", "POST", JSON.stringify(input), labelPlanSourceLinkResultSchema, key, signal),
    session: (signal) => request("/session", "GET", undefined, sessionSchema, undefined, signal),
    demoSession: (role, key, signal) => request("/demo-session", "POST", JSON.stringify({ role }), sessionSchema, key, signal),
    list: (query, signal) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) if (value !== undefined && value !== "") params.set(key, String(value));
      return request("/requests?" + params.toString(), "GET", undefined, requestListSchema, undefined, signal);
    },
    detail: (id, signal) => request("/requests/" + encodeURIComponent(id), "GET", undefined, requestDetailSchema, undefined, signal),
    create: (input, key, signal) => request("/requests", "POST", JSON.stringify(input), requestDetailSchema, key, signal),
    assign: (id, salesUserId, meta, signal) => mutation(id, "/assign", "POST", { salesUserId }, meta, signal),
    saveDraft: (id, requirements, meta, signal) => mutation(id, "/draft", "PUT", { requirements }, meta, signal),
    submitSales: (id, meta, signal) => mutation(id, "/submit-sales", "POST", {}, meta, signal),
    upload: (id, upload, meta, signal) => {
      if (upload.file === null) return Promise.reject(new LabelApiError({ code: "FILE_REQUIRED", message: "Önce bir dosya seçin." }));
      const form = new FormData();
      form.set("file", upload.file);
      form.set("kind", upload.kind);
      form.set("changeNote", upload.changeNote);
      form.set("expectedVersion", String(meta.expectedVersion));
      return request("/requests/" + encodeURIComponent(id) + "/files", "POST", form, requestDetailSchema, meta.idempotencyKey, signal);
    },
    submitReview: (id, fileVersionId, meta, signal) => mutation(id, "/submit-review", "POST", { fileVersionId }, meta, signal),
    decide: (id, input, meta, signal) => mutation(id, "/decision", "POST", input, meta, signal),
    declarePrint: (id, input, meta, signal) => mutation(id, "/print", "POST", input, meta, signal),
    reportPrintIssue: async (id, reason, meta, signal) => { await request("/requests/" + encodeURIComponent(id) + "/print-issue", "POST", JSON.stringify({ reason, expectedVersion: meta.expectedVersion }), z.object({ reported: z.literal(true) }), meta.idempotencyKey, signal); },
    reuseFile: (id, fileVersionId, meta, signal) => mutation(id, "/reuse-file", "POST", { fileVersionId }, meta, signal),
    retryNotification: (id, jobId, meta, signal) => mutation(id, "/notifications/" + encodeURIComponent(jobId) + "/retry", "POST", {}, meta, signal),
    previewPlan: (file, sheetName, key, signal) => {
      const form = new FormData();
      form.set("file", file);
      if (sheetName.trim()) form.set("sheetName", sheetName.trim());
      return request("/plan-preview", "POST", form, planPreviewSchema, key, signal);
    },
    demoSalesPlan: (signal) => request("/demo-sales-plan", "GET", undefined, planPreviewSchema.nullable(), undefined, signal),
    file: async (id, fileId, signal) => {
      const response = await transport("/requests/" + encodeURIComponent(id) + "/files/" + encodeURIComponent(fileId), "GET", undefined, undefined, signal, true);
      const blob = await response.blob();
      if (!["application/pdf", "image/png", "image/jpeg"].includes(blob.type)) throw new LabelApiError({ code: "INVALID_FILE_RESPONSE", message: "Dosyanın güvenli görüntüleme türü doğrulanamadı." });
      return blob;
    },
    requestPin: (identifier, key, signal) => request("/pin/request", "POST", JSON.stringify({ identifier }), pinChallengeSchema, key, signal),
    verifyPin: (challengeId, code, key, signal) => request("/pin/verify", "POST", JSON.stringify({ challengeId, code }), identityProofSchema, key, signal),
    loginPin: (pin, signal) => request("/pin/login", "POST", JSON.stringify({ pin }), z.object({ authenticated: z.literal(true) }), undefined, signal),
    logoutPin: async signal => { await transport("/pin/logout", "POST", JSON.stringify({}), undefined, signal); },
  };
}
